import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchLecturesQuery } from '../gql/generated-hooks';
import { createRouteWithParams } from '../helpers/urlHelpers';
import { ROUTES } from '../routes';
import './SearchInput.css';

export default function SearchInput() {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
  };

  const handleResultClick = (id: string) => {
    navigate(createRouteWithParams(ROUTES.LECTURE, { id }));
    setIsOpen(false);
  };

  // debounce search term
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // query: useSearchLecturesQuery instead of fetching all and filtering locally
  const [{ data, fetching }] = useSearchLecturesQuery({
    pause: debouncedTerm.length === 0,
    variables: {
      searchTerm: debouncedTerm,
      skip: 0,
      take: 50,
      // orderBy: 'date' // setting this will yield in ASC order
    }
  });

  const filteredLectures = data?.searchLectures;

  return (
    <div className="search-container" ref={searchRef}>
      <input
        type="text"
        className="search-input"
        placeholder="Search lectures..."
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
      />
      {isOpen && searchTerm && (
        <div className="search-results">
          {fetching ? (
            <div className="search-result-item">Loading...</div>
          ) : filteredLectures && filteredLectures.length > 0 ? (
            filteredLectures.map((lecture) => (
              <div
                key={lecture.id}
                className="search-result-item"
                onClick={() => handleResultClick(lecture.id)}
              >
                <div className="search-result-thumbnail">
                  <img src={lecture.linkToVideoThumbnail} alt={lecture.title} />
                </div>
                <div className="search-result-content">
                  <h3 className="search-result-title">{lecture.title} ({new Date(lecture.date).getFullYear()})</h3>
                  <p className="search-result-speakers">{lecture.speakers || 'Unknown speaker'}</p>
                  <p className="search-result-summary">
                    {lecture.summary ? (
                      lecture.summary.length > 120 
                        ? `${lecture.summary.substring(0, 120)}...` 
                        : lecture.summary
                    ) : 'No summary available'}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="search-result-item">No results found</div>
          )}
        </div>
      )}
    </div>
  );
}
